import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { IPncpBiddingDetailResponse, IPncpPurchaseItemResponse } from '../../../model/base/response/pncp-bidding-detail-response';

interface PncpBiddingDetailState {
  bidding: IPncpBiddingDetailResponse | null;
  loading: boolean;
  error: string | null;
  items: IPncpPurchaseItemResponse[];
  itemsPage: number;
  hasMoreItems: boolean;
  loadingItems: boolean;
  itemsError: string | null;
}

type PncpBiddingDetailAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; payload: IPncpBiddingDetailResponse }
  | { type: 'LOAD_ERROR'; payload: string }
  | { type: 'LOAD_ITEMS_START' }
  | { type: 'LOAD_ITEMS_SUCCESS'; payload: { items: IPncpPurchaseItemResponse[]; page: number; hasMore: boolean } }
  | { type: 'LOAD_ITEMS_ERROR'; payload: string };

const initialState: PncpBiddingDetailState = {
  bidding: null,
  loading: false,
  error: null,
  items: [],
  itemsPage: 0,
  hasMoreItems: false,
  loadingItems: false,
  itemsError: null,
};

const PncpBiddingDetailStateContext = createContext<PncpBiddingDetailState | undefined>(undefined);
const PncpBiddingDetailDispatchContext = createContext<React.Dispatch<PncpBiddingDetailAction> | undefined>(undefined);

function pncpBiddingDetailReducer(state: PncpBiddingDetailState, action: PncpBiddingDetailAction): PncpBiddingDetailState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...initialState, loading: true };
    case 'LOAD_SUCCESS':
      return { ...state, loading: false, bidding: action.payload };
    case 'LOAD_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'LOAD_ITEMS_START':
      return { ...state, loadingItems: true, itemsError: null };
    case 'LOAD_ITEMS_SUCCESS':
      return {
        ...state,
        loadingItems: false,
        items: action.payload.page === 1 ? action.payload.items : [...state.items, ...action.payload.items],
        itemsPage: action.payload.page,
        hasMoreItems: action.payload.hasMore,
      };
    case 'LOAD_ITEMS_ERROR':
      return { ...state, loadingItems: false, itemsError: action.payload };
    default:
      return state;
  }
}

export function PncpBiddingDetailProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(pncpBiddingDetailReducer, initialState);

  return (
    <PncpBiddingDetailStateContext.Provider value={state}>
      <PncpBiddingDetailDispatchContext.Provider value={dispatch}>
        {children}
      </PncpBiddingDetailDispatchContext.Provider>
    </PncpBiddingDetailStateContext.Provider>
  );
}

export function usePncpBiddingDetailState() {
  const context = useContext(PncpBiddingDetailStateContext);
  if (context === undefined) {
    throw new Error('usePncpBiddingDetailState must be used within a PncpBiddingDetailProvider');
  }
  return context;
}

export function usePncpBiddingDetailDispatch() {
  const context = useContext(PncpBiddingDetailDispatchContext);
  if (context === undefined) {
    throw new Error('usePncpBiddingDetailDispatch must be used within a PncpBiddingDetailProvider');
  }
  return context;
}
